/**
 * Robust counting utilities for dot detection with parameter space exploration,
 * stability analysis, and uncertainty estimation
 */

interface CountResult {
  count: number;
  params: Record<string, number | boolean>;
}

interface RobustCountEstimate {
  estimatedCount: number;
  lowerBound: number;
  upperBound: number;
  confidence: number;
  countResults: CountResult[];
  stableRegionSize: number;
  totalTrials: number;
}

/**
 * Calculate Median Absolute Deviation (MAD) for an array of numbers
 * @param values Array of numeric values
 * @returns MAD value
 */
export const calculateMAD = (values: number[]): number => {
  if (values.length === 0) return 0;
  
  // Calculate median
  const sortedValues = [...values].sort((a, b) => a - b);
  const median = sortedValues[Math.floor(sortedValues.length / 2)];
  
  // Calculate absolute deviations from median
  const deviations = sortedValues.map(v => Math.abs(v - median));
  
  // Calculate median of deviations
  const sortedDeviations = [...deviations].sort((a, b) => a - b);
  return sortedDeviations[Math.floor(sortedDeviations.length / 2)];
};

/**
 * Calculate robust z-scores using MAD
 * @param values Array of numeric values
 * @returns Array of robust z-scores
 */
export const calculateRobustZScores = (values: number[]): number[] => {
  const mad = calculateMAD(values);
  if (mad === 0) return values.map(() => 0); // Avoid division by zero
  
  const median = [...values].sort((a, b) => a - b)[Math.floor(values.length / 2)];
  return values.map(v => 0.6745 * (v - median) / mad);
};

/**
 * Remove outliers using robust z-score method
 * @param countResults Array of count results
 * @param threshold Z-score threshold for outlier removal (typically 3)
 * @returns Filtered array with outliers removed
 */
export const removeOutliers = (countResults: CountResult[], threshold = 3): CountResult[] => {
  const counts = countResults.map(result => result.count);
  const zScores = calculateRobustZScores(counts);
  
  return countResults.filter((_, i) => Math.abs(zScores[i]) <= threshold);
};

/**
 * Performs 1D DBSCAN clustering on count values
 * @param countResults Array of count results
 * @param epsilon Distance threshold for clustering
 * @param minPoints Minimum points to form a cluster
 * @returns Object with clusters and their indices
 */
export const clusterCounts = (
  countResults: CountResult[],
  epsilon: number,
  minPoints: number
): { clusters: Record<number, CountResult[]>, noise: CountResult[] } => {
  const counts = countResults.map(result => result.count);
  const n = counts.length;
  const visited = new Array(n).fill(false);
  const clusterId = new Array(n).fill(-1);
  let currentClusterId = 0;
  
  // Helper function to find neighbors
  const getNeighbors = (pointIdx: number): number[] => {
    const neighbors: number[] = [];
    const point = counts[pointIdx];
    
    for (let i = 0; i < n; i++) {
      if (Math.abs(counts[i] - point) <= epsilon) {
        neighbors.push(i);
      }
    }
    
    return neighbors;
  };
  
  // Main DBSCAN algorithm
  for (let i = 0; i < n; i++) {
    if (visited[i]) continue;
    
    visited[i] = true;
    const neighbors = getNeighbors(i);
    
    if (neighbors.length < minPoints) {
      clusterId[i] = -1; // Noise
    } else {
      clusterId[i] = currentClusterId;
      
      let j = 0;
      while (j < neighbors.length) {
        const neighborIdx = neighbors[j];
        
        if (!visited[neighborIdx]) {
          visited[neighborIdx] = true;
          const newNeighbors = getNeighbors(neighborIdx);
          
          if (newNeighbors.length >= minPoints) {
            neighbors.push(...newNeighbors.filter(idx => !neighbors.includes(idx)));
          }
        }
        
        if (clusterId[neighborIdx] === -1) {
          clusterId[neighborIdx] = currentClusterId;
        }
        
        j++;
      }
      
      currentClusterId++;
    }
  }
  
  // Organize results by cluster
  const clusters: Record<number, CountResult[]> = {};
  const noise: CountResult[] = [];
  
  for (let i = 0; i < n; i++) {
    const id = clusterId[i];
    if (id === -1) {
      noise.push(countResults[i]);
    } else {
      if (!clusters[id]) clusters[id] = [];
      clusters[id].push(countResults[i]);
    }
  }
  
  return { clusters, noise };
};

/**
 * Find the largest stable cluster of count results
 * @param countResults Array of count results
 * @returns The largest cluster and its stability metrics
 */
export const findStabilityPlateau = (
  countResults: CountResult[]
): { stableRegion: CountResult[], stabilityScore: number } => {
  if (countResults.length <= 1) {
    return { 
      stableRegion: countResults, 
      stabilityScore: countResults.length > 0 ? 1 : 0 
    };
  }
  
  // Determine appropriate epsilon based on data range
  const counts = countResults.map(r => r.count);
  const range = Math.max(...counts) - Math.min(...counts);
  const epsilon = Math.max(1, Math.floor(range * 0.05)); // 5% of range, minimum 1
  
  // Run clustering
  const { clusters } = clusterCounts(countResults, epsilon, 3);
  
  // Find the largest cluster
  let largestCluster: CountResult[] = [];
  let largestSize = 0;
  
  for (const clusterId in clusters) {
    const cluster = clusters[clusterId];
    if (cluster.length > largestSize) {
      largestSize = cluster.length;
      largestCluster = cluster;
    }
  }
  
  // Calculate stability score (inverse coefficient of variation)
  let stabilityScore = 0;
  if (largestCluster.length > 0) {
    const clusterCounts = largestCluster.map(r => r.count);
    const mean = clusterCounts.reduce((sum, c) => sum + c, 0) / clusterCounts.length;
    const stdDev = Math.sqrt(
      clusterCounts.reduce((sum, c) => sum + Math.pow(c - mean, 2), 0) / clusterCounts.length
    );
    
    // Inverse coefficient of variation, capped at 1.0
    stabilityScore = mean === 0 ? 0 : Math.min(1.0, 1 / (stdDev / mean));
  }
  
  return {
    stableRegion: largestCluster,
    stabilityScore
  };
};

/**
 * Calculate the final robust count estimate with uncertainty
 * @param countResults Array of count results after parameter sweep
 * @returns Robust count estimate with uncertainty bounds and confidence
 */
export const calculateRobustEstimate = (
  countResults: CountResult[]
): RobustCountEstimate => {
  if (countResults.length === 0) {
    return {
      estimatedCount: 0,
      lowerBound: 0,
      upperBound: 0,
      confidence: 0,
      countResults: [],
      stableRegionSize: 0,
      totalTrials: 0
    };
  }
  
  const totalTrials = countResults.length;
  
  // Remove outliers
  const filteredResults = removeOutliers(countResults, 3);
  
  // Find stability plateau
  const { stableRegion, stabilityScore } = findStabilityPlateau(filteredResults);
  
  if (stableRegion.length === 0) {
    // Fall back to median of all filtered results if no stable region found
    const allCounts = filteredResults.map(r => r.count);
    const sortedCounts = [...allCounts].sort((a, b) => a - b);
    const median = sortedCounts[Math.floor(sortedCounts.length / 2)];
    const mad = calculateMAD(allCounts);
    
    return {
      estimatedCount: median,
      lowerBound: Math.max(0, Math.round(median - 1.96 * 1.4826 * mad)),
      upperBound: Math.round(median + 1.96 * 1.4826 * mad),
      confidence: 0.5, // Low confidence because no stable region
      countResults: filteredResults,
      stableRegionSize: 0,
      totalTrials
    };
  }
  
  // Calculate statistics from stable region
  const stableCounts = stableRegion.map(r => r.count);
  const sortedCounts = [...stableCounts].sort((a, b) => a - b);
  const median = sortedCounts[Math.floor(sortedCounts.length / 2)];
  const mad = calculateMAD(stableCounts);
  
  // Calculate interquartile range
  const q1 = sortedCounts[Math.floor(sortedCounts.length * 0.25)];
  const q3 = sortedCounts[Math.floor(sortedCounts.length * 0.75)];
  const iqr = q3 - q1;
  
  // Calculate confidence as (stable region size / total filtered) * stability score
  const confidence = Math.min(1.0, (stableRegion.length / filteredResults.length) * stabilityScore);
  
  return {
    estimatedCount: median,
    lowerBound: Math.max(0, Math.round(median - 1.96 * 1.4826 * mad)), // 95% CI using MAD
    upperBound: Math.round(median + 1.96 * 1.4826 * mad), // 95% CI using MAD
    confidence,
    countResults: stableRegion,
    stableRegionSize: stableRegion.length,
    totalTrials
  };
};

/**
 * Performs a parameter sweep by generating various parameter combinations
 * and collecting count results for robust estimation
 * @param baseParams Base detection parameters
 * @param processImageFn Function that processes image with parameters and returns count
 * @param numTrials Target number of parameter combinations to try
 * @returns Array of count results from parameter sweep
 */
export const sweepParameters = async (
  baseParams: Record<string, number | boolean>,
  processImageFn: (params: Record<string, number | boolean>) => Promise<number>,
  numTrials = 100
): Promise<CountResult[]> => {
  const results: CountResult[] = [];
  const paramRanges: Record<string, [number, number, number]> = {
    // For each numeric parameter define: [min, max, steps]
    threshold: [100, 220, 10],
    saturationMin: [20, 70, 5],
    minArea: [1, 5, 3],
    maxArea: [10, 40, 6],
    minDistance: [2, 12, 5]
  };
  
  // Generate parameter combinations using Latin Hypercube-like sampling
  const paramKeys = Object.keys(paramRanges);
  const paramCombinations: Record<string, number | boolean>[] = [];
  
  // Generate parameter combinations
  for (let i = 0; i < numTrials; i++) {
    const params = { ...baseParams };
    
    // For each parameter, select a value from its range
    for (const key of paramKeys) {
      if (key in paramRanges) {
        const [min, max, steps] = paramRanges[key];
        // Divide the range into equal bins and select from each bin
        const binSize = (max - min) / steps;
        const binIndex = i % steps;
        // Add some randomness within the bin
        const value = min + binIndex * binSize + Math.random() * binSize;
        params[key] = Math.round(value);
      }
    }
    
    // Keep showBounds from base params
    params.showBounds = baseParams.showBounds;
    
    paramCombinations.push(params);
  }
  
  // Process each parameter combination
  for (const params of paramCombinations) {
    try {
      const count = await processImageFn(params);
      results.push({ count, params });
    } catch (error) {
      console.error('Error in parameter sweep:', error);
      // Continue with other parameter combinations
    }
  }
  
  return results;
};

/**
 * Multi-scale detection with cross-scale agreement
 * Processes the image at different scales and keeps only dots that appear at adjacent scales
 * @param baseParams Base detection parameters
 * @param processImageFn Function that processes image with parameters and returns dot components
 * @param scales Array of scale factors to try (e.g., [0.8, 1.0, 1.2])
 * @returns Array of components that appear in multiple scales
 */
export const multiScaleDetection = async (
  baseParams: Record<string, number | boolean>,
  processImageFn: (params: Record<string, number | boolean>, scale: number) => Promise<any[]>,
  scales = [0.8, 1.0, 1.2]
): Promise<any[]> => {
  const scaleResults: any[][] = [];
  
  // Process image at each scale
  for (const scale of scales) {
    try {
      const components = await processImageFn(baseParams, scale);
      scaleResults.push(components.map(comp => ({
        ...comp,
        // Normalize coordinates to the original scale
        centerX: comp.centerX / scale,
        centerY: comp.centerY / scale
      })));
    } catch (error) {
      console.error(`Error in multi-scale detection at scale ${scale}:`, error);
    }
  }
  
  if (scaleResults.length <= 1) {
    return scaleResults[0] || [];
  }
  
  // Find dots that appear in adjacent scales
  const matchedComponents: any[] = [];
  const minDistanceParam = baseParams.minDistance;
  const matchDistanceThreshold = typeof minDistanceParam === 'number' ? minDistanceParam : 5;
  
  for (let i = 0; i < scaleResults[0].length; i++) {
    const comp = scaleResults[0][i];
    let foundInNextScale = false;
    
    // Check if this component has a match in the next scale
    for (let j = 0; j < scaleResults[1].length; j++) {
      const nextComp = scaleResults[1][j];
      const distance = Math.sqrt(
        Math.pow(comp.centerX - nextComp.centerX, 2) + 
        Math.pow(comp.centerY - nextComp.centerY, 2)
      );
      
      if (distance <= matchDistanceThreshold) {
        foundInNextScale = true;
        break;
      }
    }
    
    if (foundInNextScale) {
      matchedComponents.push(comp);
    }
  }
  
  return matchedComponents;
};
